import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  Download,
  RefreshCw,
  Send,
  CheckCircle2,
  XCircle,
  MessageCircle,
  Loader2,
  Trash2,
  ExternalLink,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  PenLine,
  Palette,
  DownloadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import ProposalStatusBadge from "./ProposalStatusBadge";
import ProposalAssistant from "./ProposalAssistant";

function formatBRL(raw: string | number | null | undefined, currency = "BRL"): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency });
}

function fmtDateTime(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("pt-BR");
}

export default function ProposalSheet({
  proposalId,
  open,
  onOpenChange,
}: {
  proposalId: number | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { can } = usePermissions();
  const utils = trpc.useUtils();
  const canManage = can("crm:proposals_manage");
  const canApprove = can("crm:proposals_approve");

  const q = trpc.crm.proposals.get.useQuery(
    { id: proposalId ?? 0 },
    { enabled: open && !!proposalId },
  );
  const data = q.data;
  const proposal = data?.proposal;
  const status = proposal?.status;

  // ── Formulário de edição (rascunho / devolvida) ──────────────────────────
  const [title, setTitle] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [rerunAi, setRerunAi] = useState(false);
  useEffect(() => {
    if (!proposal) return;
    setTitle(proposal.title);
    const next: Record<string, string> = {};
    for (const v of data?.editableVariables ?? []) next[v.key] = proposal.variables?.[v.key] ?? "";
    setValues(next);
    setRerunAi(false);
  }, [proposal?.id, proposal?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEditable = (status === "draft" || status === "rejected") && canManage;
  const dirty = useMemo(() => {
    if (!proposal) return false;
    if (title !== proposal.title) return true;
    return Object.entries(values).some(([k, v]) => (proposal.variables?.[k] ?? "") !== v);
  }, [proposal, title, values]);

  const invalidate = () => {
    utils.crm.proposals.get.invalidate({ id: proposalId ?? 0 });
    utils.crm.proposals.list.invalidate();
    utils.crm.proposals.counts.invalidate();
    utils.crm.deals.listActivities.invalidate();
  };
  const onError = (fallback: string) => (e: unknown) => toast.error(toUserFriendlyError(e as any, fallback));

  const regenerate = trpc.crm.proposals.regenerate.useMutation({
    onSuccess: () => { toast.success("PDF regenerado"); invalidate(); },
    onError: onError("Erro ao regenerar a proposta"),
  });
  const submit = trpc.crm.proposals.submit.useMutation({
    onSuccess: () => { toast.success("Enviada para aprovação do gestor"); invalidate(); },
    onError: onError("Erro ao enviar para aprovação"),
  });
  const approve = trpc.crm.proposals.approve.useMutation({
    onSuccess: () => { toast.success("Proposta aprovada e assinada"); setApproveOpen(false); invalidate(); },
    onError: onError("Erro ao aprovar"),
  });
  const reject = trpc.crm.proposals.reject.useMutation({
    onSuccess: () => { toast.success("Proposta devolvida ao vendedor"); setRejectOpen(false); invalidate(); },
    onError: onError("Erro ao devolver"),
  });
  const sendWhatsApp = trpc.crm.proposals.sendWhatsApp.useMutation({
    onSuccess: () => { toast.success("Proposta enviada pelo WhatsApp"); setWaOpen(false); invalidate(); },
    onError: onError("Erro ao enviar pelo WhatsApp"),
  });
  const markSent = trpc.crm.proposals.markSent.useMutation({
    onSuccess: () => { toast.success("Marcada como enviada"); invalidate(); },
    onError: onError("Erro ao marcar como enviada"),
  });
  const setOutcome = trpc.crm.proposals.setOutcome.useMutation({
    onSuccess: () => { toast.success("Desfecho registrado"); invalidate(); },
    onError: onError("Erro ao registrar desfecho"),
  });
  const del = trpc.crm.proposals.delete.useMutation({
    onSuccess: () => { toast.success("Proposta excluída"); setDeleteOpen(false); onOpenChange(false); invalidate(); },
    onError: onError("Erro ao excluir"),
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [waCaption, setWaCaption] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const mySignature = trpc.crm.proposals.mySignature.useQuery(undefined, {
    enabled: open && canApprove && status === "pending_approval",
  });

  // Canva: só mostra os botões se a empresa conectou a conta.
  const canvaStatus = trpc.canva.status.useQuery(undefined, { enabled: open && canManage });
  const canvaConnected = canvaStatus.data?.connected === true;
  const openInCanva = trpc.crm.proposals.openInCanva.useMutation({
    onSuccess: (r) => {
      invalidate();
      if (r.editUrl) {
        window.open(r.editUrl, "_blank", "noopener");
        toast.success(r.created ? "Proposta importada para o Canva — abrindo o editor" : "Abrindo o design no Canva");
      } else {
        toast.success("Design criado no Canva");
      }
    },
    onError: onError("Erro ao abrir no Canva"),
  });
  const pullFromCanva = trpc.crm.proposals.pullFromCanva.useMutation({
    onSuccess: () => { toast.success("PDF atualizado com a versão do Canva"); invalidate(); },
    onError: onError("Erro ao trazer o PDF do Canva"),
  });

  const busy =
    regenerate.isPending || submit.isPending || approve.isPending || reject.isPending ||
    sendWhatsApp.isPending || markSent.isPending || setOutcome.isPending || del.isPending ||
    openInCanva.isPending || pullFromCanva.isPending;
  const fromCanva = proposal?.pdfSource === "canva";

  const pdfUrl = proposal?.pdfMediaId
    ? `/api/crm/proposals/${proposal.id}/pdf?v=${proposal.version}-${proposal.pdfMediaId}`
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[1100px] w-full p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                <span className="truncate">{proposal ? `${proposal.number} · ${proposal.title}` : "Proposta"}</span>
                {status && <ProposalStatusBadge status={status} />}
                {fromCanva && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 px-2 py-0.5 text-[11px] font-medium">
                    <Palette className="h-3 w-3" /> PDF do Canva
                  </span>
                )}
              </SheetTitle>
              <SheetDescription className="flex items-center gap-2 flex-wrap">
                {data?.deal && (
                  <Link href={`/crm/deals/${data.deal.id}`} className="inline-flex items-center gap-1 hover:underline">
                    {data.deal.title} <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
                {data?.contact && (
                  <span>· {[data.contact.name, data.contact.lastName].filter(Boolean).join(" ")}{data.contact.companyName ? ` (${data.contact.companyName})` : ""}</span>
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {q.isLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : !proposal ? (
          // `get` devolve null de propósito para proposta excluída ou fora do
          // recorte por dono — sem isto a gaveta ficava carregando para sempre.
          <div className="p-10 text-center text-sm text-muted-foreground space-y-3">
            <p>Esta proposta não está mais disponível — pode ter sido excluída ou pertence a outra pessoa.</p>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Fechar</Button>
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_380px]">
            {/* ── PDF ──────────────────────────────────────────────────── */}
            <div className="bg-muted/40 min-h-[320px] lg:min-h-0 border-b lg:border-b-0 lg:border-r">
              {pdfUrl ? (
                <iframe key={pdfUrl} src={pdfUrl} title="PDF da proposta" className="w-full h-full min-h-[320px] lg:min-h-full" />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
                  O PDF ainda não foi gerado. Revise os campos e clique em “Regenerar PDF”.
                </div>
              )}
            </div>

            {/* ── Painel lateral ───────────────────────────────────────── */}
            <div className="overflow-y-auto px-5 py-4 space-y-5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><div className="text-xs text-muted-foreground">Valor</div><div className="font-medium">{formatBRL(proposal.totalValue, proposal.currency)}</div></div>
                <div><div className="text-xs text-muted-foreground">Válida até</div><div className="font-medium">{fmtDate(proposal.validUntil)}</div></div>
                <div><div className="text-xs text-muted-foreground">Versão</div><div className="font-medium">v{proposal.version}</div></div>
                <div><div className="text-xs text-muted-foreground">Criada por</div><div className="font-medium truncate">{data?.createdBy?.name ?? "—"}</div></div>
                {data?.template && (
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Modelo</div><div className="font-medium truncate">{data.template.name}</div></div>
                )}
              </div>

              {status === "rejected" && proposal.rejectedReason && (
                <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-sm">
                  <div className="font-medium text-red-800 dark:text-red-300 flex items-center gap-1.5"><XCircle className="h-4 w-4" /> Devolvida pelo gestor</div>
                  <p className="mt-1 text-red-900/80 dark:text-red-200/80 whitespace-pre-line">{proposal.rejectedReason}</p>
                </div>
              )}
              {status === "pending_approval" && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200">
                  Aguardando aprovação do gestor desde {fmtDateTime(proposal.submittedAt)}.
                </div>
              )}
              {(status === "approved" || status === "sent" || status === "accepted" || status === "declined") && proposal.approvedAt && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-3 text-sm text-emerald-900 dark:text-emerald-200">
                  <div className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="h-4 w-4" /> Aprovada e assinada em {fmtDateTime(proposal.approvedAt)}</div>
                  {proposal.approvalNote && <p className="mt-1 whitespace-pre-line opacity-80">{proposal.approvalNote}</p>}
                  {proposal.sentAt && <p className="mt-1 opacity-80">Enviada ao cliente em {fmtDateTime(proposal.sentAt)}{proposal.sentVia ? ` (${proposal.sentVia})` : ""}.</p>}
                </div>
              )}

              {/* ── Ações ─────────────────────────────────────────────── */}
              <div className="flex flex-wrap gap-2">
                {pdfUrl && (
                  <Button asChild variant="outline" size="sm">
                    <a href={`/api/crm/proposals/${proposal.id}/pdf?download=1`} target="_blank" rel="noreferrer">
                      <Download className="h-4 w-4 mr-1.5" /> Baixar PDF
                    </a>
                  </Button>
                )}
                {isEditable && (
                  <>
                    <Button size="sm" disabled={busy || !dirty && !rerunAi} onClick={() => regenerate.mutate({ id: proposal.id, values, title, rerunAi })}>
                      {regenerate.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                      Regenerar PDF
                    </Button>
                    <Button size="sm" variant="default" disabled={busy || dirty || !proposal.pdfMediaId} onClick={() => submit.mutate({ id: proposal.id })} title={dirty ? "Regenere o PDF antes de enviar" : undefined}>
                      {submit.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                      Enviar para aprovação
                    </Button>
                    {canvaConnected && (
                      <>
                        <Button size="sm" variant="outline" disabled={busy || !proposal.pdfMediaId} onClick={() => openInCanva.mutate({ id: proposal.id })} title={proposal.canvaDesignId ? "Abrir o design existente no Canva" : "Importar o PDF como design editável no Canva"}>
                          {openInCanva.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Palette className="h-4 w-4 mr-1.5" />}
                          {proposal.canvaDesignId ? "Editar no Canva" : "Abrir no Canva"}
                        </Button>
                        {proposal.canvaDesignId && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => pullFromCanva.mutate({ id: proposal.id })} title="Exporta o design do Canva e substitui o PDF da proposta">
                            {pullFromCanva.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-1.5" />}
                            Trazer do Canva
                          </Button>
                        )}
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={busy} onClick={() => setDeleteOpen(true)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {status === "pending_approval" && canApprove && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => setApproveOpen(true)}>
                      <PenLine className="h-4 w-4 mr-1.5" /> Aprovar e assinar
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejectOpen(true)}>
                      <XCircle className="h-4 w-4 mr-1.5" /> Devolver
                    </Button>
                  </>
                )}
                {(status === "approved" || status === "sent") && canManage && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => { setWaCaption(`Olá! Segue a proposta ${proposal.number} — ${proposal.title}. Qualquer dúvida, estou à disposição.`); setWaOpen(true); }}>
                      <MessageCircle className="h-4 w-4 mr-1.5" /> {status === "sent" ? "Reenviar pelo WhatsApp" : "Enviar pelo WhatsApp"}
                    </Button>
                    {status === "approved" && (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => markSent.mutate({ id: proposal.id, via: "manual" })}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marcar como enviada
                      </Button>
                    )}
                  </>
                )}
                {status === "sent" && canManage && (
                  <>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setOutcome.mutate({ id: proposal.id, outcome: "accepted" })}>
                      <ThumbsUp className="h-4 w-4 mr-1.5" /> Cliente aceitou
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setOutcome.mutate({ id: proposal.id, outcome: "declined" })}>
                      <ThumbsDown className="h-4 w-4 mr-1.5" /> Cliente recusou
                    </Button>
                  </>
                )}
              </div>

              {/* ── Assistente ───────────────────────────────────────── */}
              {canManage && (
                <ProposalAssistant
                  proposalId={proposal.id}
                  editable={isEditable}
                  title={title}
                  values={values}
                  labels={Object.fromEntries((data?.editableVariables ?? []).map((v) => [v.key, v.label]))}
                  onApply={(c) => {
                    if (c.title) setTitle(c.title);
                    if (Object.keys(c.values).length) setValues((s) => ({ ...s, ...c.values }));
                  }}
                  onApplyAndRegenerate={(c) => {
                    const nextTitle = c.title ?? title;
                    const nextValues = { ...values, ...c.values };
                    setTitle(nextTitle);
                    setValues(nextValues);
                    regenerate.mutate({ id: proposal.id, values: nextValues, title: nextTitle, rerunAi: false });
                  }}
                />
              )}

              {/* ── Campos editáveis ──────────────────────────────────── */}
              {isEditable && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Título da proposta</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
                    </div>
                    {(data?.editableVariables ?? []).map((v) => (
                      <div key={v.key}>
                        <Label className="text-xs flex items-center gap-1.5">
                          {v.label}
                          {v.kind === "ai" && <span className="inline-flex items-center gap-0.5 text-[10px] text-primary"><Sparkles className="h-3 w-3" /> IA</span>}
                        </Label>
                        {v.multiline !== false ? (
                          <Textarea rows={v.kind === "ai" ? 6 : 3} value={values[v.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))} />
                        ) : (
                          <Input value={values[v.key] ?? ""} onChange={(e) => setValues((s) => ({ ...s, [v.key]: e.target.value }))} />
                        )}
                      </div>
                    ))}
                    {(data?.editableVariables ?? []).some((v) => v.kind === "ai") && (
                      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox checked={rerunAi} onCheckedChange={(c) => setRerunAi(c === true)} className="mt-0.5" />
                        <span>Ao regenerar, pedir para a IA reescrever os campos que eu deixar em branco.</span>
                      </label>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Dados da negociação, do contato e dos produtos são atualizados automaticamente a cada regeneração.
                    </p>
                    {fromCanva && proposal.canvaDesignId && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        O PDF atual veio do Canva. “Regenerar PDF” volta ao HTML do modelo e descarta os ajustes feitos lá — para aplicar mudanças de texto, edite no Canva e use “Trazer do Canva”.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>

      {/* ── Aprovar ───────────────────────────────────────────────────── */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprovar e assinar</DialogTitle>
            <DialogDescription>
              A sua assinatura será estampada no PDF final, com a data de hoje. O vendedor poderá enviar ao cliente em seguida.
            </DialogDescription>
          </DialogHeader>
          {mySignature.data ? (
            <div className="rounded-md border p-3 bg-white flex items-center justify-center">
              <img src={mySignature.data.imageDataUrl} alt="Sua assinatura" className="max-h-20" />
            </div>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200">
              Você ainda não cadastrou a sua assinatura.{" "}
              <Link href="/crm/proposals?tab=signature" className="underline font-medium">Cadastrar agora</Link>
            </div>
          )}
          <div>
            <Label className="text-xs">Observação (opcional)</Label>
            <Textarea rows={3} value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder="Ex.: aprovado com desconto máximo de 10%." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancelar</Button>
            <Button disabled={approve.isPending || !mySignature.data} onClick={() => proposal && approve.mutate({ id: proposal.id, note: approveNote || undefined })}>
              {approve.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PenLine className="h-4 w-4 mr-1.5" />}
              Aprovar e assinar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Devolver ──────────────────────────────────────────────────── */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Devolver ao vendedor</DialogTitle>
            <DialogDescription>Explique o que precisa mudar. A proposta volta a ser editável.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex.: o prazo de entrega está fora do que combinamos; ajuste para 45 dias." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button variant="destructive" disabled={reject.isPending || rejectReason.trim().length < 3} onClick={() => proposal && reject.mutate({ id: proposal.id, reason: rejectReason })}>
              {reject.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Devolver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── WhatsApp ──────────────────────────────────────────────────── */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar pelo WhatsApp</DialogTitle>
            <DialogDescription>
              O PDF vai para o WhatsApp do contato da negociação e fica registrado na conversa dele no Inbox. Precisa da janela de 24h aberta (o cliente ter mandado mensagem recentemente).
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Mensagem que acompanha o arquivo</Label>
            <Textarea rows={3} value={waCaption} onChange={(e) => setWaCaption(e.target.value)} maxLength={1000} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)}>Cancelar</Button>
            <Button disabled={sendWhatsApp.isPending} onClick={() => proposal && sendWhatsApp.mutate({ id: proposal.id, caption: waCaption })}>
              {sendWhatsApp.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-1.5" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Excluir ───────────────────────────────────────────────────── */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta proposta?</AlertDialogTitle>
            <AlertDialogDescription>O rascunho e o PDF serão removidos. Isso não pode ser desfeito.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => proposal && del.mutate({ id: proposal.id })}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
